// Small generic retry helper for transient/network failures. Kept at the call
// site (not in ApiClient) so we only retry the calls we know are safe to repeat
// (e.g. loading Stripe product/plan options) rather than every request.

export interface RetryOptions {
  // Number of retries AFTER the first attempt (total attempts = retries + 1).
  retries?: number
  // Base delay before the first retry, in milliseconds.
  delayMs?: number
  // Multiplier applied to the delay after each attempt (exponential backoff).
  backoff?: number
  // Optional predicate; return false to stop retrying a given error.
  shouldRetry?: (error: unknown) => boolean
}

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

export const retryAsync = async <T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> => {
  const {retries = 2, delayMs = 500, backoff = 2, shouldRetry} = options

  let lastError: unknown
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error
      const isLastAttempt = attempt === retries
      if (isLastAttempt || (shouldRetry && !shouldRetry(error))) {
        break
      }
      await wait(delayMs * Math.pow(backoff, attempt))
    }
  }
  throw lastError
}

export default retryAsync
